import React, { useEffect, useState } from 'react';
import { useDraggableY } from '@/hooks/useDraggableY';
import { cx } from '@/utils';

import SvgAccessibilityIcon from '../Icons/AccessibilityIcon';
import { useLayoutContext } from '../Layout/LayoutProvider/LayoutContext';
import { useAccessibilityContext } from '../Providers/AccessibilityProvider/AccessibilityContext';
import DecreaseSizeFont from './DecreaseSizeFont';
import DislexicOption from './DislexicOption';
import GrayscaleOption from './GrayscaleOption';
import HightContrasteOption from './HightContrasteOption';
import HightlightLink from './HightlightLink';
import IncreaseCursor from './IncreaseCursor';
import IncreaseSizeFont from './IncreaseSizeFont';
import ReadingGuide from './ReadingGuide';
import RelevoLink from './RelevoLink';
import ResetAccessibilityOption from './ResetAccessibilityOption';
import StoryTellingOption from './StoryTellingOption';
import TranslateOption from './TranslateOption';
import WrapperGrayScale from './WrapperGrayScale';

const AccessibilityButton = ({ isPreview = false }: { isPreview?: boolean }) => {
  const { position, handleMouseDown, isDrag } = useDraggableY();
  const [isVisible, setIsVisible] = useState(false);

  const {
    layout: { isGrayScaled, isHightContrast },
  } = useLayoutContext();

  const {
    storytellerEnabled,
    fontSize,
    isCursorBig,
    isDislexic,
    isHightlightLink,
    langTeller,
    languaje,
    isReadingGuideEnabled,
  } = useAccessibilityContext();

  useEffect(() => {
    const layoutParameterers = {
      isGrayScaled,
      isHightContrast,
    };
    localStorage.setItem('layoutParameters', JSON.stringify(layoutParameterers));
  }, [isGrayScaled, isHightContrast]);

  useEffect(() => {
    const parameters = {
      storytellerEnabled,
      fontSize,
      isCursorBig,
      isDislexic,
      isHightlightLink,
      langTeller,
      languaje,
      isReadingGuideEnabled,
    };

    localStorage.setItem('accessibilityParameters', JSON.stringify(parameters));
  }, [
    storytellerEnabled,
    fontSize,
    isCursorBig,
    isDislexic,
    isHightlightLink,
    langTeller,
    languaje,
    isReadingGuideEnabled,
  ]);

  const toggleVisibility = () => {
    if (!isDrag) {
      setIsVisible(!isVisible);
    }
  };

  return (
    <div
      className={cx(
        'fixed end-0 top-[35%] z-50',
        { 'w-[60px]': !isVisible },
        { 'h-[60px]': !isVisible }
      )}
      style={{ top: position?.top }}
      onMouseDown={isPreview ? undefined : handleMouseDown}
    >
      <WrapperGrayScale
        className={`relative flex flex-row transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div
          role="button"
          aria-label={isVisible ? 'Cerrar menú de accesibilidad' : 'Abrir menú de accesibilidad'}
          className="absolute left-[-60px] flex h-[60px] w-[60px] cursor-pointer items-center justify-center rounded-s-[5px] bg-primary shadow-md"
          onClick={toggleVisibility}
        >
          <SvgAccessibilityIcon className="h-[35px] w-[35px] fill-white" />
        </div>

        <div
          className={cx('flex flex-col gap-2 rounded-md bg-white p-5 shadow-md', {
            hidden: isPreview,
          })}
        >
          <StoryTellingOption />
          <IncreaseSizeFont />
          <DecreaseSizeFont />
          <GrayscaleOption />
          <HightContrasteOption />
          <DislexicOption />
          <IncreaseCursor />
          <HightlightLink />
          <ReadingGuide />
          <TranslateOption />
          <ResetAccessibilityOption />
          <RelevoLink />
        </div>
      </WrapperGrayScale>
    </div>
  );
};

export const AccessibilityOption = ({
  name,
  icon,
  onClick,
}: {
  name: string;
  icon: React.ReactNode;
  onClick?: () => void;
}) => {
  return (
    <button
      type="button"
      className="flex w-[200px] cursor-pointer items-center gap-3 border-none bg-transparent p-0 text-left"
      onClick={onClick}
    >
      <div className="flex h-[25px] w-[25px] items-center justify-center rounded-full bg-primary">
        {icon}
      </div>
      <span className="body-text1-regular cursor-pointer select-none text-[14px] !leading-[14px]">
        {name}
      </span>
    </button>
  );
};

export default AccessibilityButton;
